/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronRight, ChevronLeft, X, CheckCircle, Info, Search, Upload, Download, Camera, Video, Hash, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PhonePreview from '../components/PhonePreview';
import OtpVerificationToggle from '../components/OtpVerificationToggle';
import apiClient from '../api/client';
import toastService from '../utils/toastService';
import { validateTextField } from '../utils/validation';
import { FIELD_LIMITS } from '../utils/constants';
import { getIndustryLabel } from '../constants/workflowOptions';
import { toLocal } from '../utils/dateTime';
import filesIcon from '../assets/files.svg';
import scannerIcon from '../assets/scanner.svg';

const typeIcons = { photo: Camera, video: Video, identifier: Hash };

const formatExpiryLabel = (value, unit) => {
  if (!value || !unit) return '—';
  switch (unit) {
    case 'minutes': return `${value} minute${value !== 1 ? 's' : ''}`;
    case 'hours':   return `${value} hour${value !== 1 ? 's' : ''}`;
    case 'days':    return `${value} day${value !== 1 ? 's' : ''}`;
    default:        return `${value} ${unit}`;
  }
};

const ERR_EXPIRY_MINUTES = 'Expiry must be at least 15 minutes';
const ERR_EXPIRY_HOURS = 'Expiry must be at least 1 hour';
const ERR_EXPIRY_DAYS = 'Expiry must be at least 1 day';

const MIN_EXPIRY_MINUTES = process.env.REACT_APP_MIN_EXPIRY_MINUTES
  ? parseInt(process.env.REACT_APP_MIN_EXPIRY_MINUTES, 10)
  : 15;

const CLASS_INFO_TOOLTIP =
  'tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:w-max before:max-w-[240px] before:content-[attr(data-tip)] before:whitespace-normal before:text-left before:leading-snug after:border-r-[#fffdf2]';

const CLASS_TOGGLE_TOOLTIP =
  'tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:max-w-none before:content-[attr(data-tip)] before:whitespace-nowrap after:border-r-[#fffdf2]';

const TIME_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const convertToHours = (value, unit) => {
  switch (unit) {
    case 'minutes': return value / 60;
    case 'hours': return value;
    case 'days': return value * 24;
    default: return value;
  }
};

const SAMPLE_CSV_HEADERS = 'Recipient Name, Customer Reference Number, Phone, Email';
const SAMPLE_CSV_ROWS = [
  'John Smith,POL-123456,5550000000,john@example.com',  // all fields
  'Jane Doe,POL-789012,5550000001,',                    // no email
  'Bob Johnson,POL-345678,,bob@example.com',            // no phone
].join('\n');

const BulkSendAssignments = () => {
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [isWorkflowDropdownOpen, setIsWorkflowDropdownOpen] = useState(false);
  const [workflowSearch, setWorkflowSearch] = useState('');
  const workflowRef = useRef(null);
  const workflowSearchRef = useRef(null);

  const [creatorName, setCreatorName] = useState('');
  const [expiryValue, setExpiryValue] = useState('');
  const [expiryUnit, setExpiryUnit] = useState('hours');
  const [restrictToSingleSession, setRestrictToSingleSession] = useState(false);
  const [otpVerification, setOtpVerification] = useState(false);
  const [otpVia, setOtpVia] = useState('email');

  const [workflowPreview, setWorkflowPreview] = useState(null);
  const [previewStep, setPreviewStep] = useState(0);

  const [csvFile, setCsvFile] = useState(null);
  const [csvDragOver, setCsvDragOver] = useState(false);
  const [showCsvNote, setShowCsvNote] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantSettings, setTenantSettings] = useState(null);
  const otpTouchedRef = useRef(false);

  // Load workflows
  useEffect(() => {
    apiClient.get('/workflows', { params: { limit: 1000 } })
      .then(r => {
        const data = r.data;
        if (Array.isArray(data)) setWorkflows(data);
        else if (data && Array.isArray(data.workflows)) setWorkflows(data.workflows);
        else setWorkflows([]);
      })
      .catch(() => setWorkflows([]));
  }, []);

  // Load tenant settings once — used both for OTP defaults and as the expiry fallback below
  useEffect(() => {
    apiClient.get('/settings')
      .then(r => {
        const s = r.data;
        setTenantSettings(s);
        if (otpTouchedRef.current) return;
        if (s?.otpVerification !== undefined) setOtpVerification(!!s.otpVerification);
        if (s?.sendOtpVia) setOtpVia(s.sendOtpVia);
      })
      .catch(() => {
        // Silently fallback to defaults if unable to fetch settings
      });
  }, []);

  // Close workflow dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (workflowRef.current && !workflowRef.current.contains(event.target)) {
        setIsWorkflowDropdownOpen(false);
        setWorkflowSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // When workflow changes, populate expiry from workflow or tenant settings
  useEffect(() => {
    if (!selectedWorkflow) {
      setExpiryValue('');
      setExpiryUnit('hours');
      setWorkflowPreview(null);
      setPreviewStep(0);
      return;
    }
    const wf = workflows.find(w => w.name === selectedWorkflow);
    setWorkflowPreview(wf || null);
    setPreviewStep(0);
    if (wf?.defaultExpiry && wf?.expiryTimeUnit) {
      setExpiryValue(String(wf.defaultExpiry));
      setExpiryUnit(wf.expiryTimeUnit);
    } else if (wf?.defaultExpiryHours) {
      setExpiryValue(String(wf.defaultExpiryHours));
      setExpiryUnit('hours');
    } else if (tenantSettings?.defaultExpiry && tenantSettings?.expiryTimeUnit) {
      setExpiryValue(String(tenantSettings.defaultExpiry));
      setExpiryUnit(tenantSettings.expiryTimeUnit);
    } else if (tenantSettings?.defaultExpiryHours) {
      setExpiryValue(String(tenantSettings.defaultExpiryHours));
      setExpiryUnit('hours');
    } else {
      setExpiryValue('24');
      setExpiryUnit('hours');
    }
  }, [selectedWorkflow, workflows, tenantSettings]);

  const applyCsvFile = (file, inputEl) => {
    setError('');
    if (file) {
      const isCSV = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
      if (!isCSV) {
        setFieldErrors(prev => ({ ...prev, csvFile: 'Only CSV files are accepted' }));
        if (inputEl) inputEl.value = '';
        return;
      }
      setFieldErrors(prev => ({ ...prev, csvFile: '' }));
    }
    setCsvFile(file);
  };

  const handleCsvFileChange = (e) => {
    applyCsvFile(e.target.files?.[0] || null, e.target);
  };

  const handleCsvDrop = (e) => {
    e.preventDefault();
    setCsvDragOver(false);
    if (csvFile) return;
    applyCsvFile(e.dataTransfer.files?.[0] || null);
  };

  const handleFieldBlur = useCallback((fieldKey, value, fieldLabel, options) => {
    const errorMsg = validateTextField(value, fieldLabel, options);
    setFieldErrors(prev => ({ ...prev, [fieldKey]: errorMsg }));
  }, []);

  const validate = useCallback(() => {
    const errors = {};
    if (!selectedWorkflow) errors.selectedWorkflow = 'Please select a capture workflow';
    const creatorError = validateTextField(creatorName, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
    if (creatorError) errors.creatorName = creatorError;
    if (!csvFile) errors.csvFile = 'Please upload a CSV file';

    const parsedExpiry = parseInt(expiryValue, 10);
    if (!expiryValue || isNaN(parsedExpiry) || parsedExpiry <= 0) {
      errors.expiryValue = 'Please enter a valid positive number for expiry';
    } else {
      const hours = convertToHours(parsedExpiry, expiryUnit);
      if (expiryUnit === 'minutes' && parsedExpiry < MIN_EXPIRY_MINUTES) errors.expiryValue = ERR_EXPIRY_MINUTES;
      else if (expiryUnit === 'hours' && hours < 1) errors.expiryValue = ERR_EXPIRY_HOURS;
      else if (expiryUnit === 'days' && parsedExpiry < 1) errors.expiryValue = ERR_EXPIRY_DAYS;
    }

    return errors;
  }, [selectedWorkflow, creatorName, csvFile, expiryValue, expiryUnit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    
    setError('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('workflowName', selectedWorkflow);
      formData.append('creatorName', creatorName.trim());
      formData.append('defaultExpiry', parseInt(expiryValue, 10));
      formData.append('expiryTimeUnit', expiryUnit);
      formData.append('restrictToSingleSession', String(restrictToSingleSession));
      formData.append('requiredOtpVerification', String(otpVerification));
      if (otpVerification) formData.append('otpVia', otpVia);
      formData.append('file', csvFile);

      const res = await apiClient.post('/bulk-imports/initiate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { importId } = res.data;
      toastService.success(`Bulk import initiated successfully.`);
      navigate(`/assignments/confirm-import/${importId}`);
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('. ') : (msg || err.response?.data?.error || 'Failed to create bulk assignments'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = () => {
    const content = `${SAMPLE_CSV_HEADERS}\n${SAMPLE_CSV_ROWS}`;
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_assignments_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const items = workflowPreview?.items || [];
  const branding = workflowPreview?.branding || {};
  const totalScreens = 2 + items.length + 1;
  const screenLabels = ['Welcome', 'Steps', ...items.map((_, i) => `#${i + 1}`), 'Done'];

  return (
    <>
    {loading && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    )}
    <div className="flex flex-col lg:flex-row h-auto rounded-2xl border border-base-300 shadow-sm bg-base-100">
      {/* Left column */}
      <div className="flex-1 flex flex-col overflow-y-auto">
      {/* Header */}
      <div className="p-6 sm:p-8 border-b border-base-300 flex justify-between items-start flex-shrink-0">
        <div>
          <h1 className="page-title mb-1">Bulk Send Assignments</h1>
          <p className="page-subtitle">Upload a CSV to create multiple assignments at once</p>
        </div>
        <button
          onClick={() => navigate('/assignments')}
          className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content"
          title="Close and return to assignments"
        >
          <X size={24} />
        </button>
      </div>

      {/* Form */}
      <div className="p-6 sm:p-8 w-full overflow-y-auto">
        <div className="flex flex-col xl:flex-row gap-8">
        {/* Form Column */}
        <div className="w-full xl:w-1/2">
        <form onSubmit={handleSubmit} noValidate className="space-y-6">

          {/* Workflow */}
          <div className="relative" ref={workflowRef}>
            <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
              Select Capture Workflow *
            </span>
            <button
              type="button"
              onClick={() => setIsWorkflowDropdownOpen(!isWorkflowDropdownOpen)}
              className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-3 bg-base-100 border border-base-300 text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all cursor-pointer group min-w-0"
            >
              <span className="truncate flex-1 text-left min-w-0 block">
                {selectedWorkflow ? workflows.find(w => w.name === selectedWorkflow)?.name : 'Choose a workflow...'}
              </span>
              <ChevronRight size={14} className={`text-base-content/20 group-hover:text-primary transition-transform shrink-0 ${isWorkflowDropdownOpen ? 'rotate-90' : ''}`} />
            </button>

            {isWorkflowDropdownOpen && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col max-h-64">
                <div className="px-3 py-2 border-b border-base-content/10 flex-shrink-0">
                  <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/30" />
                    <input
                      ref={workflowSearchRef}
                      type="text"
                      value={workflowSearch}
                      onChange={e => setWorkflowSearch(e.target.value)}
                      placeholder="Search workflows..."
                      className="w-full pl-7 pr-3 py-1.5 text-xs bg-base-200 border border-base-content/10 rounded-lg outline-none focus:border-primary text-base-content placeholder-base-content/30"
                      autoFocus
                    />
                    {workflowSearch && (
                      <button type="button" onClick={() => setWorkflowSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content">
                        <X size={11} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="overflow-y-auto no-scrollbar flex-1">
                  <button
                    type="button"
                    onClick={() => { setSelectedWorkflow(''); setIsWorkflowDropdownOpen(false); setWorkflowSearch(''); }}
                    className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 flex items-center gap-2 group ${!selectedWorkflow ? 'bg-primary/5' : ''}`}
                  >
                    <span className={`text-[10px] font-black uppercase tracking-widest ${!selectedWorkflow ? 'text-primary' : 'text-base-content/30 group-hover:text-primary'}`}>Choose a workflow...</span>
                    {!selectedWorkflow && <CheckCircle size={12} className="text-primary ml-auto" />}
                  </button>
                  {workflows
                    .filter(w => w.name.toLowerCase().includes(workflowSearch.toLowerCase()))
                    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                    .map(w => (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => {
                          setSelectedWorkflow(w.name);
                          setIsWorkflowDropdownOpen(false);
                          setWorkflowSearch('');
                          setFieldErrors(prev => ({ ...prev, selectedWorkflow: '' }));
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 last:border-none flex items-center gap-2 group ${selectedWorkflow === w.name ? 'bg-primary/5' : ''}`}
                      >
                        <span className={`text-sm font-bold custom-truc ${selectedWorkflow === w.name ? 'text-primary' : 'text-base-content group-hover:text-primary'}`}>
                          {w.name}
                        </span>
                        {selectedWorkflow === w.name && <CheckCircle size={12} className="text-primary ml-auto" />}
                      </button>
                    ))}
                  {workflows.filter(w => w.name.toLowerCase().includes(workflowSearch.toLowerCase())).length === 0 && (
                    <div className="px-4 py-6 text-center">
                      <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">No workflows found</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <input type="hidden" value={selectedWorkflow} name="workflowName" />
            {fieldErrors.selectedWorkflow && (
              <p className="text-error text-xs mt-1 font-medium">{fieldErrors.selectedWorkflow}</p>
            )}
          </div>

          {/* Creator Name */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label htmlFor="creator-name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                Creator Name *
              </label>
              <div className={CLASS_INFO_TOOLTIP} data-tip="Person who creates and sends the assignments.">
                <Info size={10} className="text-base-content/30 cursor-help" />
              </div>
            </div>
            <input
              id="creator-name"
              type="text"
              value={creatorName}
              onChange={e => {
                setCreatorName(e.target.value);
                if (fieldErrors.creatorName) {
                  setFieldErrors(prev => ({ ...prev, creatorName: validateTextField(e.target.value, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
                }
              }}
              onBlur={() => handleFieldBlur('creatorName', creatorName, 'Creator name', { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT })}
              placeholder="e.g. Agent 007"
              className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content placeholder-base-content/20 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.creatorName ? 'border-error focus:ring-error focus:border-error' : 'border-base-300'}`}
            />
            {fieldErrors.creatorName && (
              <p className="text-error text-xs mt-1 font-medium">{fieldErrors.creatorName}</p>
            )}
          </div>

          {/* Expiry Time & Unit */}
          <div>
            <label htmlFor="expiry-value" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
              Expiry Time *
            </label>
            <div className="flex items-center gap-3">
              <input
                id="expiry-value"
                type="number"
                min="1"
                max="8760"
                value={expiryValue}
                onWheel={e => e.target.blur()}
                onChange={e => {
                  setExpiryValue(e.target.value);
                  if (fieldErrors.expiryValue) setFieldErrors(prev => ({ ...prev, expiryValue: '' }));
                }}
                placeholder="Enter Value"
                className={`rounded-md w-48 pl-3 pr-2 py-2.5 bg-base-100 border text-sm font-medium focus:ring-2 focus:ring-primary focus:border-primary outline-none ${fieldErrors.expiryValue ? 'border-error focus:ring-error focus:border-error' : 'border-base-300'}`}
              />
              <select
                value={expiryUnit}
                onChange={e => setExpiryUnit(e.target.value)}
                className="rounded-md pl-3 pr-8 py-2.5 bg-base-100 border border-base-300 text-sm font-medium focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                {TIME_UNITS.map(unit => (
                  <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
              </select>
            </div>
            {fieldErrors.expiryValue && (
              <p className="text-error text-xs mt-1 font-medium">{fieldErrors.expiryValue}</p>
            )}
            <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest mt-2 ml-1">
              Values auto-populated from workflow / tenant settings. Modify as needed.
            </p>
          </div>

          {/* Restrict to Single Session */}
          <div className="flex items-center justify-between px-4 py-3.5 bg-base-200/50 border border-base-300 rounded-xl">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
                Restrict to Single Session
              </span>
              <div
                className={CLASS_TOGGLE_TOOLTIP}
                data-tip="When enabled, only one device can actively use this capture link at a time."
              >
                <Info size={10} className="text-base-content/30 cursor-help" />
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={restrictToSingleSession}
                onChange={e => setRestrictToSingleSession(e.target.checked)}
              />
              <div className="relative w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
            </label>
          </div>

          {/* OTP Verification */}
          <OtpVerificationToggle
            checked={otpVerification}
            onToggleChange={setOtpVerification}
            via={otpVia}
            onViaChange={setOtpVia}
            tooltip="When enabled, every recipient in this CSV must verify a one-time code before starting capture. Rows missing the required phone/email will fail validation."
          />

          {/* CSV Upload */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                Upload CSV File *
              </label>
              <button
                type="button"
                onClick={handleDownloadSample}
                className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-primary hover:underline"
              >
                <Download size={12} />
                Download Sample CSV
              </button>
            </div>
            <label
              className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl transition-all ${csvFile ? 'cursor-default' : 'cursor-pointer hover:bg-base-200/50'} ${fieldErrors.csvFile ? 'border-error' : csvDragOver ? 'border-primary bg-primary/5' : 'border-base-content/20 hover:border-primary/50'}`}
              onClick={(e) => { if (csvFile) e.preventDefault(); }}
              onDragOver={(e) => { e.preventDefault(); if (!csvFile) setCsvDragOver(true); }}
              onDragLeave={() => setCsvDragOver(false)}
              onDrop={handleCsvDrop}
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <Upload size={24} className={csvDragOver ? 'text-primary' : 'text-base-content/30'} />
                {csvFile ? (
                  <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5">
                    <span className="text-sm font-bold text-primary truncate max-w-[240px]">{csvFile.name}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCsvFile(null); setError(''); }}
                      className="flex-shrink-0 text-primary/50 hover:text-error hover:bg-error/10 rounded-full p-0.5 transition-colors"
                      title="Remove file"
                    >
                      <X size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm font-bold text-base-content/50">
                      {csvDragOver ? 'Drop to upload' : 'Click or drag & drop CSV'}
                    </span>
                    <span className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">Only .csv files accepted</span>
                  </>
                )}
              </div>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleCsvFileChange}
                disabled={!!csvFile}
              />
            </label>
            {fieldErrors.csvFile && (
              <p className="text-error text-xs mt-1 font-medium">{fieldErrors.csvFile}</p>
            )}
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-xl text-sm font-bold">{error}</div>
          )}

          {/* CSV Field Rules Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2">
                <Info size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="font-bold">Note: Follow the CSV sample as a base and don't make any changes in the format. See field criteria: </span>
              </div>
              <button
                type="button"
                onClick={() => setShowCsvNote(v => !v)}
                className="text-amber-600 font-black uppercase tracking-widest text-[10px] hover:underline flex-shrink-0"
              >
                {showCsvNote ? 'Show less' : 'Show more'}
              </button>
            </div>
            {showCsvNote && (
              <ul className="mt-2 ml-5 space-y-1 list-disc text-amber-700 font-medium">
                <li><span className="font-bold">Recipient Name:</span> Required. No special characters. Min 3 letters.</li>
                <li><span className="font-bold">Customer Reference Number:</span> Required. Min 3 characters.</li>
                <li><span className="font-bold">Email:</span> Optional. If provided, must be a valid email address. Required for every row when OTP Verification is enabled with Send Via Email.</li>
                <li><span className="font-bold">Phone:</span> Optional. Digits only, max 10 digits. Required for every row when OTP Verification is enabled with Send Via SMS.</li>
              </ul>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/assignments')}
              className="flex-1 min-h-[56px] px-5 rounded-2xl border border-base-300 bg-base-100 text-base-content/70 font-semibold text-sm hover:bg-base-200 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 min-h-[56px] px-5 rounded-2xl bg-primary text-primary-content font-bold text-sm tracking-wide shadow-sm hover:scale-[1.01] hover:shadow-md transition-all disabled:opacity-50 disabled:hover:scale-100"
            >
              {loading ? 'Processing...' : 'Send Bulk Assignments'}
            </button>
          </div>
        </form>
        </div>

        {/* Template Preview Column */}
        <div className="w-full xl:w-1/2">
          {workflowPreview && items.length > 0 && (
            <div className="border border-primary/20 bg-primary/5 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Template Preview</p>
                <span className="text-[10px] font-black text-primary/60 uppercase tracking-widest bg-primary/10 px-2 py-1 rounded-lg">
                  {items.length} Screens &bull; {getIndustryLabel(workflowPreview.industry)}
                </span>
              </div>
              <div className="space-y-3">
                {items.map((item, index) => {
                  const Icon = typeIcons[item.type] || Camera;
                  const typeColors = { photo: 'bg-primary/10 text-primary', video: 'bg-secondary/10 text-secondary' };
                  return (
                    <div
                      key={item.id}
                      onClick={() => setPreviewStep(2 + index)}
                      className="flex items-center gap-4 bg-base-100 rounded-xl p-3 border border-base-300 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
                    >
                      <span className="text-xs text-base-content/20 font-black w-4 flex-shrink-0 text-center group-hover:text-primary/40">{(index + 1).toString().padStart(2, '0')}</span>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeColors[item.type] || 'bg-base-200 text-base-content/40'}`}>
                        {item.type === 'identifier' ? <img src={scannerIcon} alt="" className="w-10 h-10 object-contain p-1" /> : item.type === 'file' ? <img src={filesIcon} alt="" className="w-10 h-10 object-contain p-2" /> : <Icon size={18} />}
                      </div>
                      <span className="text-sm text-base-content/80 font-black tracking-tight flex-1 custom-line-break">{item.description}</span>
                      {!item.required && (
                        <span className="text-[9px] font-black uppercase tracking-widest text-base-content/20 bg-base-200 px-2.5 py-1 rounded-full flex-shrink-0">Optional</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {workflowPreview.description && (
                <p className="text-xs text-primary/60 mt-4 font-bold tracking-widest px-4 custom-line-break">{workflowPreview.description}</p>
              )}
              {expiryValue && parseInt(expiryValue, 10) > 0 && (
                <div className="flex items-center gap-3 mt-6 pt-5 border-t border-primary/10">
                  <Clock size={14} className="text-primary/40 flex-shrink-0" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-primary/40">
                    Link Lifetime: <span className="text-primary">{formatExpiryLabel(parseInt(expiryValue, 10), expiryUnit)}</span>
                    <span className="mx-2 opacity-50">&bull;</span>
                    {toLocal(new Date(Date.now() + convertToHours(parseInt(expiryValue, 10), expiryUnit) * 3600000), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
      </div>

      {/* Right: Phone Preview */}
      {workflowPreview ? (
        <div className="w-full lg:w-[360px] xl:w-[400px] flex-shrink-0 bg-base-200 flex flex-col border-t lg:border-t-0 border-base-300">
          <div className="px-4 py-3 border-b border-base-300 bg-base-100 flex justify-between flex-shrink-0">
            <p className="text-[10px] font-black text-base-content/30 uppercase tracking-[0.2em]">Live Preview</p>
            <p className="text-[10px] text-base-content/20 uppercase font-black tracking-widest">Interactive view</p>
          </div>
          <div className="px-3 py-2 bg-base-100 border-b border-base-300 flex-shrink-0">
            <div className="flex flex-wrap gap-2">
              {screenLabels.map((label, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPreviewStep(i)}
                  className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
                    previewStep === i ? 'text-white bg-primary shadow-lg scale-105' : 'bg-base-200 text-base-content/40 hover:bg-base-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-2">
            <PhonePreview
              workflowName={workflowPreview.name}
              name={workflowPreview.branding?.welcomeTitle || 'Welcome, {name}'}
              submitterName=""
              assigneeName={creatorName}
              customerReferenceNumber=""
              customerReferenceLabel={undefined}
              systemReferenceNumber=""
              industry={workflowPreview.industry}
              description={workflowPreview.description}
              items={items}
              previewStep={previewStep}
              branding={branding}
              expiresAt={new Date(Date.now() + (expiryValue ? parseInt(expiryValue, 10) : 24) * (expiryUnit === 'minutes' ? 60000 : expiryUnit === 'days' ? 86400000 : 3600000))}
              globalInstructionUrl={workflowPreview.globalInstructionUrl}
            />
          </div>
          <div className="px-5 py-1 bg-base-100 border-t border-base-300 flex items-center justify-between flex-shrink-0">
            <button
              type="button"
              onClick={() => setPreviewStep(p => Math.max(0, p - 1))}
              disabled={previewStep === 0}
              className="p-1.5 rounded-lg hover:bg-base-200 disabled:opacity-20 text-base-content transition"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">{previewStep + 1} / {Math.max(1, totalScreens)}</span>
            <button
              type="button"
              onClick={() => setPreviewStep(p => Math.min(totalScreens - 1, p + 1))}
              disabled={previewStep >= totalScreens - 1}
              className="p-1.5 rounded-lg hover:bg-base-200 disabled:opacity-20 text-base-content transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="w-full lg:w-[360px] xl:w-[400px] flex-shrink-0 bg-base-200 hidden lg:flex flex-col items-center justify-center text-center p-8 border-l border-base-300">
          <div className="text-5xl mb-6 opacity-40">📱</div>
          <p className="text-xs font-black text-base-content/40 uppercase tracking-[0.2em]">Select a workflow</p>
          <p className="text-[10px] text-base-content/20 mt-2 uppercase font-black tracking-widest">Preview will sync automatically</p>
        </div>
      )}
    </div>
    </>
  );
};

export default BulkSendAssignments;
